// src/components/Navbar.js

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';

const Navbar = ({ scrolled, isMenuOpen, setIsMenuOpen }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Menu structure with paths
  const menuItems = [
    {
      title: 'Szynkowski Consulting',
      submenu: [
        { name: 'Pozyskiwanie dofinansowań', path: '/pozyskiwanie-dofinansowan' },
        { name: 'Rozliczanie dofinansowań', path: '/rozliczanie-dofinansowan' },
        { name: 'Pożyczki unijne', path: '/pozyczki-unijne' },
        { name: 'Doradztwo strategiczne', path: '/doradztwo-strategiczne' },
        { name: 'Biznes plany i analizy finansowe', path: '/biznes-plany' }
      ]
    },
    {
      title: 'Szynkowski Legal',
      submenu: [
        { name: 'Obsługa prawna biznesu', path: '/obsluga-prawna-biznesu' },
        { name: 'Nowe technologie i innowacje', path: '/nowe-technologie' },
        { name: 'Podatki i finansowanie przedsiębiorstw', path: '/podatki-finansowanie' },
        { name: 'Pomoc publiczna i fundusze unijne', path: '/pomoc-publiczna' },
        { name: 'Prawo spółek i M&A', path: '/prawo-spolek' },
        { name: 'Rozwiązywanie sporów', path: '/rozwiazywanie-sporow' },
        { name: 'Własność intelektualna i ochrona danych osobowych', path: '/wlasnosc-intelektualna' }
      ]
    },
    {
      title: 'O nas',
      path: '/o-nas'
    },
    {
      title: 'Kontakt',
      path: '/kontakt'
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleContactScroll = () => {
    // If we're not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const contactSection = document.querySelector('#kontakt');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const contactSection = document.querySelector('#kontakt');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const handleNavClick = (path) => {
    if (path.startsWith('/#')) {
      const sectionId = path.substring(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const section = document.querySelector(`#${sectionId}`);
          if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const section = document.querySelector(`#${sectionId}`);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const toggleMobileDropdown = (index) => {
    setMobileActiveDropdown(mobileActiveDropdown === index ? null : index);
  };

  return (
    <>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'backdrop-blur-md bg-black/80 py-3 border-b border-white/10' 
          : 'backdrop-blur-sm bg-black/50 py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" aria-label="Strona główna">
              <div className="font-bold text-2xl">
                <img 
                  src="/logo.png" 
                  alt="Szynkowski & Company" 
                  className={`transition-all duration-500 w-auto ${
                    scrolled ? 'h-10 md:h-12' : 'h-12 md:h-16'
                  }`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://szynkowski.com.pl/wp-content/uploads/2025/06/Szynkowski-new.png";
                  }}
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8" ref={dropdownRef}>
              {menuItems.map((item, index) => (
                <div key={index} className="relative">
                  {item.submenu ? (
                    <>
                      <button
                        className="flex items-center gap-1 text-gray-300 hover:text-white font-medium transition-all duration-300 relative group py-2"
                        onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                        onMouseEnter={() => setActiveDropdown(index)}
                      >
                        {item.title}
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-violet-700 group-hover:w-full transition-all duration-300"></span>
                      </button>
                      
                      <div 
                        className={`absolute top-full left-0 mt-2 w-80 transition-all duration-300 ${
                          activeDropdown === index ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                        }`}
                        onMouseLeave={() => setActiveDropdown(null)}
                      >
                        <div className="backdrop-blur-xl bg-black/90 border border-white/20 rounded-xl shadow-2xl overflow-hidden">
                          {item.submenu.map((subitem, subindex) => (
                            <Link
                              key={subindex}
                              to={subitem.path}
                              className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200 border-b border-white/10 last:border-0"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {subitem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    item.path.startsWith('/#') ? (
                      <button
                        onClick={() => handleNavClick(item.path)}
                        className="text-gray-300 hover:text-white font-medium transition-all duration-300 relative group py-2"
                      >
                        {item.title}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-violet-700 group-hover:w-full transition-all duration-300"></span>
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className="text-gray-300 hover:text-white font-medium transition-all duration-300 relative group py-2"
                      >
                        {item.title}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-violet-500 to-violet-700 group-hover:w-full transition-all duration-300"></span>
                      </Link>
                    )
                  )}
                </div>
              ))}
              
              <button 
                onClick={handleContactScroll}
                className={`group relative px-6 py-2.5 bg-black/20 backdrop-blur-sm border border-white/20 rounded-lg transition-all duration-300 hover:bg-violet-600 hover:border-violet-600 hover:scale-105 ${
                  scrolled ? 'text-sm' : ''
                }`}
              >
                <span className="relative z-10 flex items-center gap-2 text-white font-semibold">
                  Bezpłatna konsultacja
                  <Sparkles className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
                </span>
              </button>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 -mr-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setIsMenuOpen(false)}></div>
          <div className="absolute right-0 top-0 h-dvh max-h-dvh w-full sm:w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 shadow-2xl overflow-y-auto overscroll-contain">
            
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-6 right-6 p-2 text-white hover:bg-white/10 rounded-lg transition-colors z-20"
            >
              <X size={24} />
            </button>
            
            {/* Mobile Menu Items */}
            <div className="p-4 sm:p-6 mt-16 pb-10">
              {menuItems.map((item, index) => (
                <div key={index} className="mb-2">
                  {item.submenu ? (
                    <div>
                      <button
                        onClick={() => toggleMobileDropdown(index)}
                        className="w-full flex items-center justify-between gap-3 text-left text-base sm:text-lg font-semibold text-white py-3 px-3 sm:px-4 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        {item.title}
                        <ChevronRight 
                          className={`w-5 h-5 transition-transform duration-300 ${
                            mobileActiveDropdown === index ? 'rotate-90' : ''
                          }`} 
                        />
                      </button>
                      
                      <div className={`mt-1 ml-4 border-l border-white/10 transition-all duration-300 overflow-hidden ${
                        mobileActiveDropdown === index 
                          ? 'max-h-96 opacity-100' 
                          : 'max-h-0 opacity-0'
                      }`}>
                        {item.submenu.map((subitem, subindex) => (
                          <Link
                            key={subindex}
                            to={subitem.path}
                            className="block pl-4 pr-3 py-2 text-sm sm:text-base text-gray-300 hover:text-white hover:bg-white/5 rounded-r-lg transition-all leading-snug"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {subitem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    item.path.startsWith('/#') ? (
                      <button
                        onClick={() => {
                          handleNavClick(item.path);
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left text-lg font-semibold text-white py-3 px-4 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        {item.title}
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className="block text-base sm:text-lg font-semibold text-white py-3 px-3 sm:px-4 rounded-lg hover:bg-white/5 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.title}
                      </Link>
                    )
                  )}
                </div>
              ))}
              
              <button 
                onClick={handleContactScroll}
                className="w-full mt-6 relative px-5 py-3.5 bg-black/20 backdrop-blur-sm border border-white/20 rounded-lg transition-all duration-300 hover:bg-violet-600 hover:border-violet-600 group"
              >
                <span className="relative z-10 flex items-center justify-center gap-2 text-white font-semibold">
                  Bezpłatna konsultacja
                  <Sparkles className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;